/**
 * Player Context
 * 
 * Centralized state management for player data across the application.
 * Provides player state, position, and actions with Firestore persistence.
 * Falls back to localStorage if Firestore is unavailable.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Player, CombatState, PendingEncounter } from '../types/player';
import { AxialCoord, TerrainType } from '../types/terrain';
import { QuestState, Choice } from '../types/campaign.types';
import { useAuth } from './AuthContext';
import type { StorageService } from '../services/storage';
import { LocalStorageService, FirestoreStorageService } from '../services/storage';
import { StorySnippet } from '../services/storyGeneration';
import { ensurePlayerReady } from '../utils/dailyReset';
import * as questManager from '../utils/questManager';

// ============================================================================
// Types
// ============================================================================

interface PlayerState {
  player: Player;
  position: AxialCoord;
  exploredHexes: Set<string>; // Set of "q,r" strings for explored hexes
  seenNarratives: Array<{ hexKey: string; contentId: string }>; // Terrain-based narrative tracking
  travelState: {
    destinationHex: AxialCoord;
    arrivalTime: number; // Unix timestamp in milliseconds
    route: AxialCoord[]; // Full path of hexes to traverse
    currentRouteIndex: number; // Which hex along route we're on (0 = start)
    nextHexArrivalTime: number; // When we arrive at the next hex in route
    singleHexTravelTimeMs: number; // Time to travel one hex (constant for the journey)
  } | null;
  // Deprecated but kept for compatibility
  foragingState: {
    startTime: number;
    completionTime: number;
    terrainType: TerrainType;
  } | null;
  restingState: {
    startTime: number;
    completionTime: number;
  } | null;
  
  // Sync Fields
  combatState: CombatState | null;
  pendingEncounter: PendingEncounter | null;
  currentStory: StorySnippet | null; // Current exploration story (persists across reloads)
  activeQuestId: string | null;
  questState: {
    currentSectionIndex: number;
    currentChoices: Choice[];
    questVariables: Record<string, any>;
  } | null;
  
  // Quest Playthrough (full quest data with sections)
  questPlaythrough: any | null; // questManager.QuestPlaythrough
  questDataLoaded: boolean; // Flag to indicate quest data has been loaded from Firestore
}

interface PlayerContextValue {
  player: Player;
  position: AxialCoord;
  exploredHexes: Set<string>;
  seenNarratives: Array<{ hexKey: string; contentId: string }>;
  travelState: PlayerState['travelState'];
  foragingState: PlayerState['foragingState'];
  restingState: PlayerState['restingState'];
  
  // Sync Fields
  combatState: CombatState | null;
  pendingEncounter: PendingEncounter | null;
  currentStory: StorySnippet | null;
  activeQuestId: string | null;
  questState: PlayerState['questState'];
  questPlaythrough: any | null; // questManager.QuestPlaythrough
  questDataLoaded: boolean;
  
  loading: boolean; // Loading state for async operations
  // Actions
  movePlayer: (newPosition: AxialCoord, staminaCost: number) => void;
  consumeRations: (amount: number) => void;
  addRations: (amount: number) => void;
  takeDamage: (amount: number) => void;
  heal: (amount: number) => void;
  addGold: (amount: number) => void;
  spendGold: (amount: number) => boolean; // Returns false if insufficient gold
  restoreStamina: (amount: number) => void;
  updatePlayer: (updates: Partial<Player>) => void;
  updatePosition: (newPosition: AxialCoord) => void;
  markHexExplored: (hex: AxialCoord) => void;
  markHexesExplored: (hexes: AxialCoord[]) => void;
  isHexExplored: (hex: AxialCoord) => boolean;
  getExplorationCount: () => number;
  resetPlayer: () => void;
  
  // Terrain Narrative Actions
  recordSeenNarrative: (hexKey: string, contentId: string) => void;
  getSeenNarrativeForHex: (hexKey: string) => string | null;
  
  // Sync State Setters
  setCombatState: (state: CombatState | null) => void;
  setPendingEncounter: (state: PendingEncounter | null) => void;
  setCurrentStory: (story: StorySnippet | null) => void;
  setQuestState: (state: PlayerState['questState']) => void;
  setActiveQuestId: (id: string | null) => void;
  updateQuestPlaythrough: (playthrough: any | null) => void; // questManager.QuestPlaythrough
  saveStateImmediately: (stateOverride?: Partial<PlayerState>) => Promise<void>; // Save state immediately, optionally with overrides

  // Travel actions
  startTravel: (route: AxialCoord[], singleHexTravelTimeMs: number) => void;
  completeTravel: () => void;
  cancelTravel: () => void;
  advanceTravelToHex: (hex: AxialCoord) => void; // Advance to next hex or stop at specific hex
  stopTravelAtHex: (hex: AxialCoord) => void; // Stop travel at specific hex (for monster encounters)
  isTraveling: () => boolean;
  getTravelTimeRemaining: () => number;
  getTravelProgress: () => { current: number; total: number } | null;
  // Foraging actions
  startForaging: (terrain: TerrainType, durationMs?: number) => void;
  completeForaging: () => void;
  isForaging: () => boolean;
  getForagingTimeRemaining: () => number;
  // Resting actions
  startResting: (durationMs?: number) => void;
  completeResting: () => void;
  isResting: () => boolean;
  getRestingTimeRemaining: () => number;
}

// ============================================================================
// Context
// ============================================================================

const PlayerContext = createContext<PlayerContextValue | undefined>(undefined);

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'storyworld_player';
const MIGRATION_FLAG_KEY = 'storyworld_migrated_to_firestore';
const DEFAULT_PLAYER: Player = {
  id: '1',
  name: 'Alex Lux',
  stamina: 12,
  maxStamina: 12,
  hp: 60,
  maxHp: 60,
  rations: 10,
  maxRations: 10,
  gold: 10,
  hasSeenOnboarding: false,
};
const DEFAULT_POSITION: AxialCoord = { q: 0, r: 0 };

// ============================================================================
// Helper Functions
// ============================================================================

function createDefaultState(): PlayerState {
  const startingExplored = new Set<string>();
  startingExplored.add(`${DEFAULT_POSITION.q},${DEFAULT_POSITION.r}`);
  
  return {
    player: DEFAULT_PLAYER,
    position: DEFAULT_POSITION,
    exploredHexes: startingExplored,
    seenNarratives: [],
    travelState: null,
    foragingState: null,
    restingState: null,
    combatState: null,
    pendingEncounter: null,
    currentStory: null,
    activeQuestId: null,
    questState: null,
    questPlaythrough: null,
    questDataLoaded: false,
  };
}

function dataToState(data: { 
  player?: any; 
  position?: AxialCoord; 
  exploredHexes?: string[];
  seenNarratives?: Array<{ hexKey: string; contentId: string }>; 
  travelState?: any; 
  foragingState?: any; 
  restingState?: any;
  combatState?: CombatState | null;
  pendingEncounter?: PendingEncounter | null;
  currentStory?: StorySnippet | null;
  activeQuestId?: string | null;
  questState?: any;
  questPlaythrough?: any;
  questDataLoaded?: boolean;
}): PlayerState {
  
  // DEBUG: Log onboarding field locations
  console.log('🔍 dataToState - Loading hasSeenOnboarding:', {
    fromNestedPlayer: data.player?.hasSeenOnboarding,
    fromTopLevel: (data as any).hasSeenOnboarding,
    willUse: data.player?.hasSeenOnboarding ?? (data as any).hasSeenOnboarding ?? DEFAULT_PLAYER.hasSeenOnboarding
  });
  const exploredHexes = new Set<string>(data.exploredHexes || []);
  const seenNarratives = data.seenNarratives || [];
  
  // Handle legacy travelState that doesn't have route info
  let processedTravelState: { 
    destinationHex: AxialCoord; 
    arrivalTime: number; 
    route: AxialCoord[]; 
    currentRouteIndex: number; 
    nextHexArrivalTime: number;
    singleHexTravelTimeMs: number;
  } | null = null;
  
  if (data.travelState) {
    if (!data.travelState.route) {
      // Legacy format - convert to new format with single-hex route
      const timeRemaining = Math.max(0, data.travelState.arrivalTime - Date.now());
      processedTravelState = {
        destinationHex: data.travelState.destinationHex,
        arrivalTime: data.travelState.arrivalTime,
        route: [data.position || DEFAULT_POSITION, data.travelState.destinationHex],
        currentRouteIndex: 0,
        nextHexArrivalTime: data.travelState.arrivalTime,
        singleHexTravelTimeMs: timeRemaining, // Use remaining time for legacy
      };
    } else {
      // New format - ensure all fields are present
      // For legacy data that has route but not singleHexTravelTimeMs, calculate it
      const hasTime = typeof data.travelState.singleHexTravelTimeMs === 'number';
      const calculatedTime = hasTime ? data.travelState.singleHexTravelTimeMs : Math.max(0, (data.travelState.nextHexArrivalTime ?? data.travelState.arrivalTime) - Date.now());
      
      processedTravelState = {
        destinationHex: data.travelState.destinationHex,
        arrivalTime: data.travelState.arrivalTime,
        route: data.travelState.route,
        currentRouteIndex: data.travelState.currentRouteIndex ?? 0,
        nextHexArrivalTime: data.travelState.nextHexArrivalTime ?? data.travelState.arrivalTime,
        singleHexTravelTimeMs: calculatedTime ?? 5000, // Default to 5 seconds if undefined
      };
    }
  }
  
  return {
    player: {
      ...DEFAULT_PLAYER,
      ...data.player,
      // Ensure all required fields are present
      id: data.player?.id || DEFAULT_PLAYER.id,
      name: data.player?.name || DEFAULT_PLAYER.name,
      stamina: data.player?.stamina ?? DEFAULT_PLAYER.stamina,
      maxStamina: data.player?.maxStamina ?? DEFAULT_PLAYER.maxStamina,
      hp: data.player?.hp ?? DEFAULT_PLAYER.hp,
      maxHp: data.player?.maxHp ?? DEFAULT_PLAYER.maxHp,
      rations: data.player?.rations ?? DEFAULT_PLAYER.rations,
      maxRations: data.player?.maxRations ?? DEFAULT_PLAYER.maxRations,
      gold: data.player?.gold ?? DEFAULT_PLAYER.gold,
      // Check both nested and top-level for hasSeenOnboarding
      hasSeenOnboarding: data.player?.hasSeenOnboarding ?? (data as any).hasSeenOnboarding ?? DEFAULT_PLAYER.hasSeenOnboarding,
      // Preserve spawn hex (check both spawnHex and legacy homeHex)
      spawnHex: data.player?.spawnHex || data.player?.homeHex,
    },
    position: data.position || DEFAULT_POSITION,
    exploredHexes,
    seenNarratives,
    travelState: processedTravelState,
    foragingState: data.foragingState || null,
    restingState: data.restingState || null,
    combatState: data.combatState || null,
    pendingEncounter: data.pendingEncounter || null,
    currentStory: data.currentStory || null,
    activeQuestId: data.activeQuestId || null,
    questState: data.questState || null,
    questPlaythrough: data.questPlaythrough || null,
    questDataLoaded: data.questDataLoaded || false,
  };
}

function stateToData(state: PlayerState) {
  return {
    player: state.player,
    position: state.position,
    exploredHexes: Array.from(state.exploredHexes),
    seenNarratives: state.seenNarratives,
    travelState: state.travelState,
    foragingState: state.foragingState,
    restingState: state.restingState,
    combatState: state.combatState,
    pendingEncounter: state.pendingEncounter,
    currentStory: state.currentStory,
    activeQuestId: state.activeQuestId,
    questState: state.questState,
    questPlaythrough: state.questPlaythrough,
    questDataLoaded: state.questDataLoaded,
  };
}

// ============================================================================
// Provider
// ============================================================================

interface PlayerProviderProps {
  children: ReactNode;
}

export function PlayerProvider({ children }: PlayerProviderProps): React.ReactElement {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<PlayerState>(createDefaultState);
  const [loading, setLoading] = useState(true);
  const [storageService, setStorageService] = useState<StorageService | null>(null);

  // Initialize storage service based on auth state
  useEffect(() => {
    if (authLoading) return;

    if (user) {
      // Use Firestore when authenticated
      setStorageService(new FirestoreStorageService(user));
    } else {
      // Fallback to localStorage
      setStorageService(new LocalStorageService());
    }
  }, [user, authLoading]);

  // Load player state on mount and when storage service changes
  useEffect(() => {
    if (!storageService || authLoading) return;

    const loadData = async () => {
      try {
        setLoading(true);
        
        // Try to load from current storage service
        const data = await storageService.loadPlayerState();
        
        console.log('🔍 [QUEST SYNC DEBUG] Raw data from Firestore:', {
          hasData: !!data,
          activeQuestId: data?.activeQuestId,
          questState: data?.questState,
        });
        
        if (data) {
          const loadedState = dataToState(data);
          
          console.log('🔍 [QUEST SYNC DEBUG] Loaded state after dataToState:', {
            activeQuestId: loadedState.activeQuestId,
            questState: loadedState.questState,
          });
          
          // DEBUG: Log loaded combat state
          console.log('📥 LOADED FROM FIRESTORE - Combat State:', {
            exists: !!loadedState.combatState,
            isActive: loadedState.combatState?.isActive,
            round: loadedState.combatState?.round,
            monster: loadedState.combatState?.monster?.name,
            monsterHp: loadedState.combatState?.monster?.currentHp,
            playerHp: loadedState.combatState?.player?.hp,
            storyLines: loadedState.combatState?.storyLines?.length || 0,
          });
          console.log('📥 LOADED FROM FIRESTORE - Pending Encounter:', {
            exists: !!loadedState.pendingEncounter,
            monsterName: loadedState.pendingEncounter?.monsterName,
            encounterHex: loadedState.pendingEncounter?.hex,
            currentPosition: loadedState.position,
          });
          
          // If combat is active, clear pendingEncounter (combat has already started)
          // This ensures the UI shows combat directly instead of the story/fight button
          if (loadedState.combatState?.isActive && loadedState.pendingEncounter) {
            console.log('🔄 Combat is active, clearing pendingEncounter');
            loadedState.pendingEncounter = null;
          }
          
          // If pendingEncounter exists, validate it against current position
          // This prevents fighting the wrong monster after reloading at a different position
          if (loadedState.pendingEncounter) {
            const currentPos = loadedState.position;
            
            // Clear if no hex field (old data format) or if position doesn't match
            if (!loadedState.pendingEncounter.hex) {
              console.log('🗑️ Clearing old-format pendingEncounter (missing hex field):', {
                monsterName: loadedState.pendingEncounter.monsterName,
                currentPosition: `(${currentPos.q}, ${currentPos.r})`,
              });
              loadedState.pendingEncounter = null;
            } else {
              const encounterHex = loadedState.pendingEncounter.hex;
              if (encounterHex.q !== currentPos.q || encounterHex.r !== currentPos.r) {
                console.log('🗑️ Clearing stale pendingEncounter (position mismatch):', {
                  encounterHex: `(${encounterHex.q}, ${encounterHex.r})`,
                  currentPosition: `(${currentPos.q}, ${currentPos.r})`,
                  monsterName: loadedState.pendingEncounter.monsterName,
                });
                loadedState.pendingEncounter = null;
              } else {
                console.log('✅ Keeping valid pendingEncounter:', {
                  monsterName: loadedState.pendingEncounter.monsterName,
                  position: `(${encounterHex.q}, ${encounterHex.r})`,
                });
              }
            }
          }
          
          // Check if travel was completed while offline
          if (loadedState.travelState && loadedState.travelState.nextHexArrivalTime < Date.now()) {
            console.log('Travel completed while offline, arriving at destination');
            // Complete the travel immediately - jump to final destination
            loadedState.position = loadedState.travelState.destinationHex;
            loadedState.travelState = null;
          }
          
          // Check if foraging was completed while offline
          if (loadedState.foragingState && loadedState.foragingState.completionTime < Date.now()) {
            console.log('Foraging completed while offline');
            // Foraging completion will be handled by the component
            // We keep the state so the component can process the rewards
          }
          
          // Check if resting was completed while offline
          if (loadedState.restingState && loadedState.restingState.completionTime < Date.now()) {
            console.log('Resting completed while offline');
            // Resting completion will be handled by the component
            // We keep the state so the component can process the HP rewards
          }
          
          setState(loadedState);
          
          // ===== CHECK FOR DAILY RESET ON LOGIN =====
          // Only check for authenticated users with Firestore
          if (user && storageService instanceof FirestoreStorageService) {
            try {
              console.log('🌅 Checking for daily reset on login...');
              const resetResult = await ensurePlayerReady();
              
              if (resetResult.resetApplied) {
                console.log('✨ Daily reset applied on login:', {
                  staminaRestored: resetResult.staminaRestored,
                  hpRestored: resetResult.hpRestored,
                  message: resetResult.message,
                });
                
                // Update state with reset values
                setState(prevState => ({
                  ...prevState,
                  player: {
                    ...prevState.player,
                    stamina: resetResult.player.stamina,
                    hp: resetResult.player.hp,
                  },
                }));
                
                // Show message to user (optional - could add a toast notification here)
                console.log(`📢 ${resetResult.message}`);
              } else {
                console.log('⏰ No daily reset needed yet');
              }
            } catch (error) {
              console.error('⚠️ Failed to check daily reset on login:', error);
              // Don't block login if reset check fails - it will be checked on movement
            }
            
            // ===== CHECK FOR ACTIVE QUEST ON LOGIN =====
            // Sync quest state across devices - if player has active quest, load it immediately
            try {
              console.log('🔍 [QUEST SYNC DEBUG] Checking for active quest on login...');
              console.log('🔍 [QUEST SYNC DEBUG] User ID:', user.uid);
              
              const activePlaythrough = await questManager.getActiveQuestPlaythrough(user.uid);
              
              console.log('🔍 [QUEST SYNC DEBUG] Playthrough from DB:', activePlaythrough);
              
              if (activePlaythrough) {
                // Load all sections for this playthrough from cache
                // Sections are stored separately for caching/reuse across players
                const sections = await questManager.loadSectionsForPlaythrough(activePlaythrough);
                activePlaythrough.sections = sections;
                
                console.log('✅ [QUEST SYNC DEBUG] Active quest found on login:', {
                  playthroughId: activePlaythrough.id,
                  questId: activePlaythrough.questId,
                  currentSection: activePlaythrough.currentSection,
                  status: activePlaythrough.status,
                  hasSections: !!activePlaythrough.sections,
                  sectionsLength: activePlaythrough.sections?.length,
                });
                
                // Update state with quest IDs to ensure sync across devices
                // This ensures quest UI shows immediately on Device B if active on Device A
                const newQuestState = activePlaythrough.questState ? {
                  currentSectionIndex: activePlaythrough.currentSection,
                  currentChoices: [],
                  questVariables: activePlaythrough.questState,
                } : null;
                
                console.log('🔍 [QUEST SYNC DEBUG] Setting quest state to:', {
                  activeQuestId: activePlaythrough.questId || null,
                  questState: newQuestState,
                  questPlaythrough: !!activePlaythrough,
                });
                
                setState(prevState => ({
                  ...prevState,
                  activeQuestId: activePlaythrough.questId || null,
                  questState: newQuestState,
                  questPlaythrough: activePlaythrough,
                  questDataLoaded: true,
                }));
                
                console.log('📢 [QUEST SYNC DEBUG] Quest state synced on login - complete');
              } else {
                console.log('ℹ️ [QUEST SYNC DEBUG] No active quest found in questPlaythroughs collection');
                // Mark as loaded even when no quest is active
                setState(prevState => ({
                  ...prevState,
                  questPlaythrough: null,
                  questDataLoaded: true,
                }));
              }
            } catch (error) {
              console.error('⚠️ [QUEST SYNC DEBUG] Failed to check quest state on login:', error);
              // Don't block login if quest check fails - it will be checked in ActionUI
            }
          }
        } else if (user && storageService instanceof FirestoreStorageService) {
          // No Firestore data, check localStorage for migration
          const localStorageService = new LocalStorageService();
          const migrated = localStorage.getItem(MIGRATION_FLAG_KEY);
          
          if (!migrated) {
            // Migrate from localStorage to Firestore
            const localData = await localStorageService.loadPlayerState();
            if (localData) {
              const migratedState = dataToState(localData);
              setState(migratedState);
              // Save to Firestore
              await storageService.savePlayerState(stateToData(migratedState));
              // Mark as migrated
              localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
              console.log('Migrated player data from localStorage to Firestore');
            }
          }
        }
        
        // If still no data, use defaults
        if (!data && (!user || localStorage.getItem(MIGRATION_FLAG_KEY))) {
          setState(createDefaultState());
        }
      } catch (error) {
        console.error('Failed to load player state:', error);
        // Fallback to localStorage if Firestore fails
        if (storageService instanceof FirestoreStorageService) {
          const localStorageService = new LocalStorageService();
          const localData = await localStorageService.loadPlayerState();
          if (localData) {
            setState(dataToState(localData));
          } else {
            setState(createDefaultState());
          }
        } else {
          setState(createDefaultState());
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [storageService, authLoading, user]);

  // Debounced persistence
  useEffect(() => {
    if (!storageService || loading || authLoading) return;

    const debounceStartTimestamp = performance.now();
    console.log(`💾 [INVESTIGATION] Debounce timer started at ${new Date().toISOString()} for state change`);
    if (state.combatState) {
      console.log(`   📊 Combat state queued for save: round=${state.combatState.round}, active=${state.combatState.isActive}`);
    }

    const timeoutId = setTimeout(async () => {
      const saveStartTimestamp = performance.now();
      console.log(`💾 [INVESTIGATION] Firestore save beginning at T+${(saveStartTimestamp - debounceStartTimestamp).toFixed(2)}ms (after 500ms debounce)`);
      try {
        // DEBUG: Log combat state being saved
        if (state.combatState) {
          console.log('💾 [INVESTIGATION] SAVING TO FIRESTORE - Combat State:', {
            isActive: state.combatState.isActive,
            round: state.combatState.round,
            monster: state.combatState.monster?.name,
            monsterHp: state.combatState.monster?.currentHp,
            playerHp: state.combatState.player?.hp,
            storyLines: state.combatState.storyLines?.length || 0,
            timestamp: new Date().toISOString(),
          });
        }
        
        // DEBUG: Log quest state being saved
        console.log('💾 [QUEST SYNC DEBUG] SAVING TO FIRESTORE - Quest State:', {
          activeQuestId: state.activeQuestId,
          questState: state.questState,
          hasQuestData: !!state.activeQuestId,
          timestamp: new Date().toISOString(),
        });
        
        await storageService.savePlayerState(stateToData(state));
        const saveEndTimestamp = performance.now();
        console.log(`✅ [INVESTIGATION] Firestore save completed at T+${(saveEndTimestamp - debounceStartTimestamp).toFixed(2)}ms (save duration: ${(saveEndTimestamp - saveStartTimestamp).toFixed(2)}ms)`);
      } catch (error) {
        console.error('Failed to save player state:', error);
        // Fallback to localStorage if Firestore fails
        if (storageService instanceof FirestoreStorageService) {
          const localStorageService = new LocalStorageService();
          try {
            await localStorageService.savePlayerState(stateToData(state));
          } catch (localError) {
            console.error('Failed to save to localStorage fallback:', localError);
          }
        }
      }
    }, 500); // Debounce by 500ms for Firestore

    return () => clearTimeout(timeoutId);
  }, [state, storageService, loading, authLoading]);

  // ============================================================================
  // Actions
  // ============================================================================

  const movePlayer = useCallback((newPosition: AxialCoord, staminaCost: number) => {
    setState(prev => {
      // Auto-explore: mark new hex and neighbors as explored
      const newExplored = new Set(prev.exploredHexes);
      newExplored.add(`${newPosition.q},${newPosition.r}`);
      
      // Also explore adjacent hexes (they're visible from this position)
      const neighbors = [
        { q: newPosition.q + 1, r: newPosition.r },
        { q: newPosition.q - 1, r: newPosition.r },
        { q: newPosition.q, r: newPosition.r + 1 },
        { q: newPosition.q, r: newPosition.r - 1 },
        { q: newPosition.q + 1, r: newPosition.r - 1 },
        { q: newPosition.q - 1, r: newPosition.r + 1 },
      ];
      
      neighbors.forEach(neighbor => {
        newExplored.add(`${neighbor.q},${neighbor.r}`);
      });
      
      return {
        ...prev,
        position: newPosition,
        exploredHexes: newExplored,
        player: {
          ...prev.player,
          stamina: Math.max(0, prev.player.stamina - staminaCost),
        },
      };
    });
  }, []);

  const consumeRations = useCallback((amount: number) => {
    setState(prev => {
      const newRations = Math.max(0, prev.player.rations - amount);
      const staminaToRestore = (prev.player.rations - newRations) * 1; // Each ration restores 1 stamina
      const newStamina = Math.min(prev.player.maxStamina, prev.player.stamina + staminaToRestore);
      
      return {
        ...prev,
        player: {
          ...prev.player,
          rations: newRations,
          stamina: newStamina,
        },
      };
    });
  }, []);

  const addRations = useCallback((amount: number) => {
    setState(prev => {
      const newRations = Math.min(prev.player.maxRations, prev.player.rations + amount);
      
      return {
        ...prev,
        player: {
          ...prev.player,
          rations: newRations,
        },
      };
    });
  }, []);

  const takeDamage = useCallback((amount: number) => {
    setState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        hp: Math.max(0, prev.player.hp - amount),
      },
    }));
  }, []);

  const heal = useCallback((amount: number) => {
    setState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        hp: Math.min(prev.player.maxHp, prev.player.hp + amount),
      },
    }));
  }, []);

  const addGold = useCallback((amount: number) => {
    setState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        gold: prev.player.gold + amount,
      },
    }));
  }, []);

  const spendGold = useCallback((amount: number): boolean => {
    let success = false;
    setState(prev => {
      if (prev.player.gold >= amount) {
        success = true;
        return {
          ...prev,
          player: {
            ...prev.player,
            gold: prev.player.gold - amount,
          },
        };
      }
      return prev; // No change if insufficient gold
    });
    return success;
  }, []);

  const restoreStamina = useCallback((amount: number) => {
    setState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        stamina: Math.min(prev.player.maxStamina, prev.player.stamina + amount),
      },
    }));
  }, []);

  const updatePlayer = useCallback((updates: Partial<Player>) => {
    console.log('🔄 updatePlayer called:', {
      updates,
      currentStamina: updates.stamina !== undefined ? `${updates.stamina} (was in state)` : 'not changed',
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    });
    
    setState(prev => ({
      ...prev,
      player: {
        ...prev.player,
        ...updates,
      },
    }));
  }, []);

  const updatePosition = useCallback((newPosition: AxialCoord) => {
    setState(prev => ({
      ...prev,
      position: newPosition,
    }));
  }, []);

  const markHexExplored = useCallback((hex: AxialCoord) => {
    setState(prev => {
      const newExplored = new Set(prev.exploredHexes);
      newExplored.add(`${hex.q},${hex.r}`);
      return {
        ...prev,
        exploredHexes: newExplored,
      };
    });
  }, []);

  const markHexesExplored = useCallback((hexes: AxialCoord[]) => {
    setState(prev => {
      const newExplored = new Set(prev.exploredHexes);
      hexes.forEach(hex => {
        newExplored.add(`${hex.q},${hex.r}`);
      });
      return {
        ...prev,
        exploredHexes: newExplored,
      };
    });
  }, []);

  const isHexExplored = useCallback((hex: AxialCoord): boolean => {
    return state.exploredHexes.has(`${hex.q},${hex.r}`);
  }, [state.exploredHexes]);

  const getExplorationCount = useCallback((): number => {
    return state.exploredHexes.size;
  }, [state.exploredHexes]);

  // ============================================================================
  // Terrain Narrative Actions
  // ============================================================================

  const recordSeenNarrative = useCallback((hexKey: string, contentId: string) => {
    setState(prev => {
      // Check if this hex already has a narrative recorded
      const existing = prev.seenNarratives.find(n => n.hexKey === hexKey);
      if (existing) {
        console.log(`Hex ${hexKey} already has narrative ${existing.contentId}, not updating`);
        return prev; // Don't add duplicate
      }
      
      console.log(`📝 Recording narrative for hex ${hexKey}: contentId ${contentId}`);
      return {
        ...prev,
        seenNarratives: [...prev.seenNarratives, { hexKey, contentId }],
      };
    });
  }, []);

  const getSeenNarrativeForHex = useCallback((hexKey: string): string | null => {
    const narrative = state.seenNarratives.find(n => n.hexKey === hexKey);
    return narrative ? narrative.contentId : null;
  }, [state.seenNarratives]);

  const resetPlayer = useCallback(() => {
    const startingExplored = new Set<string>();
    startingExplored.add(`${DEFAULT_POSITION.q},${DEFAULT_POSITION.r}`);
    setState({
      player: DEFAULT_PLAYER,
      position: DEFAULT_POSITION,
      exploredHexes: startingExplored,
      seenNarratives: [],
      travelState: null,
      foragingState: null,
      restingState: null,
      combatState: null,
      pendingEncounter: null,
      currentStory: null,
      activeQuestId: null,
      questState: null,
      questPlaythrough: null,
      questDataLoaded: false,
    });
  }, []);

  // ============================================================================
  // Sync Actions
  // ============================================================================

  const setCombatState = useCallback((combatState: CombatState | null) => {
    const timestamp = new Date().toISOString();
    // DEBUG: Log when combat state is being set/cleared
    if (combatState === null) {
      console.log(`🗑️ [INVESTIGATION] setCombatState called with NULL at ${timestamp}`);
      console.trace('Stack trace for combat state clear');
    } else {
      console.log(`✅ [INVESTIGATION] setCombatState called at ${timestamp} with:`, {
        round: combatState.round,
        monster: combatState.monster?.name,
        isActive: combatState.isActive,
      });
    }
    setState(prev => ({
      ...prev,
      combatState,
    }));
  }, []);

  const setPendingEncounter = useCallback((pendingEncounter: PendingEncounter | null) => {
    if (pendingEncounter === null) {
      console.log('🗑️ setPendingEncounter called with NULL - clearing pending encounter');
      console.trace('Stack trace for pending encounter clear');
    } else {
      console.log('✅ setPendingEncounter called with encounter:', {
        monsterName: pendingEncounter.monsterName,
        hasStoryText: !!pendingEncounter.storyText,
        hasAudio: !!pendingEncounter.audioUrl,
      });
    }
    setState(prev => ({
      ...prev,
      pendingEncounter,
    }));
  }, []);

  const setCurrentStory = useCallback((currentStory: StorySnippet | null) => {
    console.log('📖 setCurrentStory called:', currentStory ? 'story set' : 'story cleared');
    setState(prev => ({
      ...prev,
      currentStory,
    }));
  }, []);

  const setQuestState = useCallback((questState: PlayerState['questState']) => {
    setState(prev => ({
      ...prev,
      questState,
    }));
  }, []);

  const setActiveQuestId = useCallback((id: string | null) => {
    setState(prev => ({
      ...prev,
      activeQuestId: id,
    }));
  }, []);

  const updateQuestPlaythrough = useCallback((playthrough: any | null) => {
    // Update the full quest playthrough and derived state
    setState(prev => ({
      ...prev,
      questPlaythrough: playthrough,
      activeQuestId: playthrough?.questId || null,
      questState: playthrough ? {
        currentSectionIndex: playthrough.currentSection,
        currentChoices: playthrough.sections?.[playthrough.sections.length - 1]?.choices || [],
        questVariables: playthrough.questState || {},
      } : null,
    }));
  }, []);

  // ============================================================================
  // Immediate Save (bypasses debounce for critical actions like combat)
  // ============================================================================

  const saveStateImmediately = useCallback(async (stateOverride?: Partial<PlayerState>) => {
    if (!storageService || loading || authLoading) {
      console.warn('⚠️ [SAVE] Cannot save: storage service not ready');
      return;
    }

    const saveStartTimestamp = performance.now();
    console.log(`💾 [INVESTIGATION] Immediate save beginning at ${new Date().toISOString()}`);
    
    // Use overridden state if provided, otherwise use current state
    const stateToSave = stateOverride ? { ...state, ...stateOverride } : state;
    
    try {
      if (stateToSave.combatState) {
        console.log('💾 [INVESTIGATION] Immediately saving combat state:', {
          isActive: stateToSave.combatState.isActive,
          round: stateToSave.combatState.round,
          monster: stateToSave.combatState.monster?.name,
          timestamp: new Date().toISOString(),
        });
      }
      
      await storageService.savePlayerState(stateToData(stateToSave));
      const saveEndTimestamp = performance.now();
      console.log(`✅ [INVESTIGATION] Immediate save completed in ${(saveEndTimestamp - saveStartTimestamp).toFixed(2)}ms`);
    } catch (error) {
      console.error('❌ [SAVE] Failed to save player state immediately:', error);
      // Fallback to localStorage if Firestore fails
      if (storageService instanceof FirestoreStorageService) {
        const localStorageService = new LocalStorageService();
        try {
          await localStorageService.savePlayerState(stateToData(stateToSave));
          console.log('✅ [SAVE] Saved to localStorage fallback');
        } catch (localError) {
          console.error('❌ [SAVE] Failed to save to localStorage fallback:', localError);
          throw localError; // Re-throw so caller knows it failed
        }
      } else {
        throw error; // Re-throw so caller knows it failed
      }
    }
  }, [state, storageService, loading, authLoading]);

  // ============================================================================
  // Travel Actions
  // ============================================================================

  const startTravel = useCallback((route: AxialCoord[], singleHexTravelTimeMs: number) => {
    if (route.length < 2) {
      console.error('Route must have at least 2 hexes (start and destination)');
      return;
    }
    
    const destination = route[route.length - 1];
    const startHex = route[0];
    const movementHexes = route.length - 1; // Don't count starting hex
    const totalTravelTime = movementHexes * singleHexTravelTimeMs;
    const arrivalTime = Date.now() + totalTravelTime;
    const nextHexArrivalTime = Date.now() + singleHexTravelTimeMs;
    
    // DEBUG: Log travel start
    console.log('🚶 TRAVEL DEBUG - Travel started');
    console.log(`   Start hex: (${startHex.q}, ${startHex.r})`);
    console.log(`   End hex: (${destination.q}, ${destination.r})`);
    console.log(`   Route length: ${route.length} hexes`);
    console.log(`   Total travel time: ${totalTravelTime}ms`);
    console.log(`   Single hex time: ${singleHexTravelTimeMs}ms`);
    console.log(`   Route:`, route.map(h => `(${h.q},${h.r})`).join(' → '));
    
    console.log('🚶 startTravel called:', {
      route,
      movementHexes,
      destination,
      stackTrace: new Error().stack
    });
    
    setState(prev => {
      // Calculate total stamina cost (1 per hex moved, not counting start)
      const staminaCost = movementHexes;
      
      console.log('💰 STAMINA DEDUCTION:', {
        before: prev.player.stamina,
        cost: staminaCost,
        after: Math.max(0, prev.player.stamina - staminaCost),
        route: route.map(h => `(${h.q},${h.r})`).join(' → ')
      });
      
      // Don't mark hexes as explored yet - this will happen when animation completes
      // This prevents the "flashing" effect where adjacent hexes highlight before the player marker arrives
      
      return {
        ...prev,
        travelState: {
          destinationHex: destination,
          arrivalTime,
          route,
          currentRouteIndex: 0, // Start at index 0 (starting position)
          nextHexArrivalTime,
          singleHexTravelTimeMs, // Store for consistent hex-to-hex timing
        },
        player: {
          ...prev.player,
          stamina: Math.max(0, prev.player.stamina - staminaCost), // Deduct all stamina upfront
        },
      };
    });
  }, []);

  const completeTravel = useCallback(() => {
    setState(prev => {
      if (!prev.travelState) return prev;
      
      // For multi-hex travel, jump directly to final destination
      return {
        ...prev,
        position: prev.travelState.destinationHex,
        travelState: null,
      };
    });
  }, []);

  const cancelTravel = useCallback(() => {
    setState(prev => {
      if (!prev.travelState) return prev;
      
      // Cancel travel - stay at current position, stamina already spent
      return {
        ...prev,
        travelState: null,
      };
    });
  }, []);

  // Advance travel to the next hex in the route, or stop if at destination
  const advanceTravelToHex = useCallback((hex: AxialCoord) => {
    setState(prev => {
      if (!prev.travelState) return prev;
      
      const { route, currentRouteIndex, singleHexTravelTimeMs, destinationHex } = prev.travelState;
      const nextIndex = currentRouteIndex + 1;
      
      // Check if we've reached the destination
      if (nextIndex >= route.length) {
        // Reached final destination
        const isInstantTravel = singleHexTravelTimeMs === 0;
        
        console.log('🏁 TRAVEL DEBUG - Reached final destination');
        console.log(`   Final hex: (${hex.q}, ${hex.r})`);
        console.log(`   Start hex was: (${route[0].q}, ${route[0].r})`);
        console.log(`   End hex: (${destinationHex.q}, ${destinationHex.r})`);
        console.log(`   Travel time per hex: ${singleHexTravelTimeMs}ms`);
        console.log(`   Is instant travel: ${isInstantTravel}`);
        
        if (isInstantTravel) {
          // 0ms travel: Update position immediately (no animation will play)
          console.log('   ✅ Instant travel - updating position immediately');
          return {
            ...prev,
            position: hex,
            travelState: null,
          };
        } else {
          // Travel with time: Let animation system handle position update
          console.log('   ⏱️ Timed travel - letting animation system handle position update');
          return {
            ...prev,
            // DON'T update position - animation system will update after animation
            travelState: null,
          };
        }
      }
      
      // Advance to next hex in route (intermediate hex, not final destination)
      const nextHex = route[nextIndex];
      const newNextHexArrivalTime = Date.now() + singleHexTravelTimeMs;
      
      console.log(`➡️ TRAVEL DEBUG - Advanced to intermediate hex in route`);
      console.log(`   Current hex: (${hex.q}, ${hex.r})`);
      console.log(`   Next hex: (${nextHex.q}, ${nextHex.r})`);
      console.log(`   Route index: ${nextIndex} of ${route.length}`);
      console.log(`   Next arrival time: ${new Date(newNextHexArrivalTime).toISOString()}`);
      console.log('   ✅ Updating position for intermediate hex (for monster encounters, etc.)');
      
      // For intermediate hexes (not final destination), update position immediately
      // This is needed for monster encounters and hex-by-hex progression
      return {
        ...prev,
        position: hex,
        travelState: {
          ...prev.travelState,
          currentRouteIndex: nextIndex,
          nextHexArrivalTime: newNextHexArrivalTime,
        },
      };
    });
  }, []);

  // Stop travel at a specific hex (used when monster encounter occurs)
  const stopTravelAtHex = useCallback((hex: AxialCoord) => {
    setState(prev => {
      if (!prev.travelState) return prev;
      
      const { route, destinationHex, currentRouteIndex } = prev.travelState;
      
      // Calculate stamina refund for hexes not traveled
      // currentRouteIndex is the index we're currently at (before moving to next hex)
      // So if we stopped at hex arrival, we've traveled to currentRouteIndex + 1
      const hexesTraveled = currentRouteIndex + 1; // Number of hexes actually moved
      const totalPlannedHexes = route.length - 1; // Total hexes planned (excluding start)
      const hexesNotTraveled = totalPlannedHexes - hexesTraveled;
      
      // Calculate refund (don't exceed max stamina)
      const staminaRefund = hexesNotTraveled;
      const newStamina = Math.min(
        prev.player.maxStamina,
        prev.player.stamina + staminaRefund
      );
      
      // DEBUG: Log travel stop and refund
      console.log('🛑 TRAVEL DEBUG - Travel stopped at hex');
      console.log(`   Stopped at: (${hex.q}, ${hex.r})`);
      console.log(`   Start hex was: (${route[0].q}, ${route[0].r})`);
      console.log(`   End hex was: (${destinationHex.q}, ${destinationHex.r})`);
      console.log(`   Hexes traveled: ${hexesTraveled} of ${totalPlannedHexes}`);
      console.log(`   Hexes not traveled: ${hexesNotTraveled}`);
      console.log(`   Stamina refund: +${staminaRefund}`);
      console.log(`   Stamina: ${prev.player.stamina} → ${newStamina}`);
      
      // Stop travel, update position to encounter hex, refund stamina
      return {
        ...prev,
        position: hex,
        travelState: null,
        player: {
          ...prev.player,
          stamina: newStamina,
        },
      };
    });
  }, []);

  const isTraveling = useCallback((): boolean => {
    return state.travelState !== null;
  }, [state.travelState]);

  const getTravelTimeRemaining = useCallback((): number => {
    if (!state.travelState) return 0;
    const remaining = state.travelState.nextHexArrivalTime - Date.now();
    return Math.max(0, remaining);
  }, [state.travelState]);

  const getTravelProgress = useCallback((): { current: number; total: number } | null => {
    if (!state.travelState) return null;
    return {
      current: state.travelState.currentRouteIndex + 1, // +1 because index is 0-based
      total: state.travelState.route.length,
    };
  }, [state.travelState]);

  // ============================================================================
  // Foraging Actions
  // ============================================================================

  const startForaging = useCallback((terrain: TerrainType, durationMs: number = 60 * 60 * 1000) => {
    const startTime = Date.now();
    const completionTime = startTime + durationMs; // Use parameter instead of hardcoded
    
    setState(prev => ({
      ...prev,
      foragingState: {
        startTime,
        completionTime,
        terrainType: terrain,
      },
    }));
  }, []);

  const completeForaging = useCallback(() => {
    setState(prev => ({
      ...prev,
      foragingState: null,
    }));
  }, []);

  const isForaging = useCallback((): boolean => {
    return state.foragingState !== null;
  }, [state.foragingState]);

  const getForagingTimeRemaining = useCallback((): number => {
    if (!state.foragingState) return 0;
    const remaining = state.foragingState.completionTime - Date.now();
    return Math.max(0, remaining);
  }, [state.foragingState]);

  // ============================================================================
  // Resting Actions
  // ============================================================================

  const startResting = useCallback((durationMs: number = 60 * 60 * 1000) => {
    const startTime = Date.now();
    const completionTime = startTime + durationMs;
    
    setState(prev => ({
      ...prev,
      restingState: {
        startTime,
        completionTime,
      },
    }));
  }, []);

  const completeResting = useCallback(() => {
    setState(prev => ({
      ...prev,
      restingState: null,
    }));
  }, []);

  const isResting = useCallback((): boolean => {
    return state.restingState !== null;
  }, [state.restingState]);

  const getRestingTimeRemaining = useCallback((): number => {
    if (!state.restingState) return 0;
    const remaining = state.restingState.completionTime - Date.now();
    return Math.max(0, remaining);
  }, [state.restingState]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: PlayerContextValue = {
    player: state.player,
    position: state.position,
    exploredHexes: state.exploredHexes,
    seenNarratives: state.seenNarratives,
    travelState: state.travelState,
    foragingState: state.foragingState,
    restingState: state.restingState,
    
    // Sync Fields
    combatState: state.combatState,
    pendingEncounter: state.pendingEncounter,
    currentStory: state.currentStory,
    activeQuestId: state.activeQuestId,
    questState: state.questState,
    questPlaythrough: state.questPlaythrough,
    questDataLoaded: state.questDataLoaded,
    
    loading: loading || authLoading,
    movePlayer,
    consumeRations,
    addRations,
    takeDamage,
    heal,
    addGold,
    spendGold,
    restoreStamina,
    updatePlayer,
    updatePosition,
    markHexExplored,
    markHexesExplored,
    isHexExplored,
    getExplorationCount,
    resetPlayer,
    recordSeenNarrative,
    getSeenNarrativeForHex,
    
    // Sync Actions
    setCombatState,
    setPendingEncounter,
    setCurrentStory,
    setQuestState,
    setActiveQuestId,
    updateQuestPlaythrough,
    saveStateImmediately,

    startTravel,
    completeTravel,
    cancelTravel,
    advanceTravelToHex,
    stopTravelAtHex,
    isTraveling,
    getTravelTimeRemaining,
    getTravelProgress,
    startForaging,
    completeForaging,
    isForaging,
    getForagingTimeRemaining,
    startResting,
    completeResting,
    isResting,
    getRestingTimeRemaining,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function usePlayer(): PlayerContextValue {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
}

